import { useNavigate, useSearch } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { useCallback, useState } from 'react';
import { AuthService, type OAuthVendor } from '@/entities/auth/auth-service';

import { showDialog } from '@/shared/utils/show-dialog';
import { logger } from '@sentry/react';
import { useSignInWithGoogle } from '@/entities/auth/hooks';
import { signInWithMicrosoft } from '@/entities/auth/sign-in-with-microsoft';
import { fetchUser } from '@/entities/auth/fetch-user';
import { REDIRECT_PAGE } from '@/shared/redirect/redirect-constants';

export const SSO_SIGN_IN_TIMEOUT_MS = 180_000;

export function useScoutSignIn() {
  const { redirect_uri, state } = useSearch({
    from: '/login/scout',
  });
  const navigate = useNavigate({ from: '/login/scout' });

  const [signInLoading, setSignInLoading] = useState(false);

  const handleSocialLoginError = useCallback(async (vendor: OAuthVendor, error: unknown) => {
    if (error instanceof HTTPError) {
      const errorBody: {
        code: number;
        data: null;
        message: string;
      } = await error.response.json();
      if (errorBody.code === 101) {
        showDialog({
          title: 'Please use your work email',
          message:
            'Personal email addresses are not allowed for registration. Please sign up with your work email (e.g., user@yourcompany.com).',
          confirmLabel: 'Confirm',
          hideCancel: true,
        });
        return;
      }

      logger.error(`${vendor} sign-in API error`, {
        extra: errorBody,
      });
    } else if (error instanceof Error) {
      logger.error(`${vendor} sign-in unexpected error`, {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });
    }

    showDialog({
      title: 'Login failed',
      message: 'An unknown error occurred. Please contact support if the problem persists.',
      confirmLabel: 'Confirm',
      hideCancel: true,
    });
  }, []);
  const { signInWithGoogle } = useSignInWithGoogle({
    timeoutMs: SSO_SIGN_IN_TIMEOUT_MS,
  });

  const handleSigninWithScout = useCallback(
    async (
      signInReqest: () => Promise<string>,
      vendor: OAuthVendor,
      scoutRedirectParams: {
        redirectUri: string;
        state: string;
      },
    ) => {
      try {
        setSignInLoading(true);
        const oauthToken = await signInReqest();
        const authTokens = await AuthService.login(oauthToken, vendor);
        const user = await fetchUser();

        if (user.workspaces.length === 0) {
          await navigate({
            to: '/workspace/create/name',
            search: { redirectPage: REDIRECT_PAGE.SETTINGS_PLAN },
          });
          setSignInLoading(false);
          return;
        }

        const deeplinkUrl = new URL(scoutRedirectParams.redirectUri);
        deeplinkUrl.searchParams.set('state', scoutRedirectParams.state);
        deeplinkUrl.searchParams.set('access_token', authTokens.accessToken);
        deeplinkUrl.searchParams.set('refresh_token', authTokens.refreshToken);

        window.location.href = deeplinkUrl.toString();

        setSignInLoading(false);
      } catch (error) {
        await handleSocialLoginError(vendor, error);
        setSignInLoading(false);
      }
    },
    [handleSocialLoginError, navigate],
  );

  const onSignInWithGoogleClick = () => {
    handleSigninWithScout(signInWithGoogle, 'google', {
      redirectUri: redirect_uri,
      state,
    });
  };

  const onSignInWithMicrosoftClick = () => {
    handleSigninWithScout(
      () => signInWithMicrosoft({ timeoutMs: SSO_SIGN_IN_TIMEOUT_MS }),
      'microsoft',
      {
        redirectUri: redirect_uri,
        state,
      },
    );
  };

  return {
    signInLoading,
    onSignInWithGoogleClick,
    onSignInWithMicrosoftClick,
  };
}
